import { Text } from "components/Typography/text.styled";
import { Container1, Container2, Row } from "./organizationalStructure.styled";
import { useCallback, useState } from "react";
import { Color } from "config/colors";
import { CustomIcon } from "components/icon/customIcon.component";
import { useTranslation } from "react-i18next";
import { FontFamily, FontSize } from "config/font";
import { Team, Unit } from "model/OrganizationalStructure";
import { EditModal } from "./editModal.component";

type UnitProps = {
  type: "unit";
  data: Unit;
  entity?: {
    units?: Unit;
  };
  onToggle: () => void;
  isOpen: boolean;
  memberCount?: number;
};

type TeamProps = {
  type: "team";
  data: Team;
  entity?: {
    units?: Unit;
  };
  onToggle: () => void;
  isOpen: boolean;
  memberCount?: number;
};

type Props = UnitProps | TeamProps;

export const StructureDropDownRow: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const isUnitData = props.type === "unit";
  const [showEditModal, setShowEditModal] = useState(false);

  const handleToggleDetails = useCallback(() => {
    props.onToggle();
  }, [props.onToggle]);

  const getCount = () => {
    if (isUnitData) {
      return (props.data as Unit).workGroups.length;
    } else {
      return props.memberCount !== undefined
        ? props.memberCount
        : (props.data as Team).workers?.length || 0;
    }
  };

  const getUnitMembersCount = () => {
    if (isUnitData) {
      const unit = props.data as Unit;

      return unit.workGroups.reduce((total, group) => {
        return total + (group.workers?.length || 0);
      }, 0);
    } else {
      return props.memberCount !== undefined
        ? props.memberCount
        : (props.data as Team).workers?.length || 0;
    }
  };

  const getCountLabel = () => {
    return isUnitData
      ? t("OrganizationalStructure.Teams")
      : t("OrganizationalStructure.Workers");
  };

  return (
    <>
      <Row key={props.data.id} team={!isUnitData}>
        <Container1>
          <Text
            style={{
              padding: 10,
              backgroundColor: Color.LightGrey03,
              borderRadius: 5,
              marginRight: 25,
            }}
          >
            {isUnitData
              ? t("OrganizationalStructure.Unit")
              : t("OrganizationalStructure.Team")}
          </Text>
          <Text
            fontFamily={FontFamily.MontserratSemiBold}
            fontSize={FontSize.Standard}
            style={{ marginTop: 10 }}
          >
            {props.data.name === "Unhandled"
              ? t("OrganizationalStructure.Unhandled")
              : props.data.name}
          </Text>
          <Text fontSize={FontSize.Small} style={{ marginTop: 10 }}>
            {`${getCount()} ${getCountLabel()}`}
          </Text>
          {props.type === "unit" && (
            <Text fontSize={FontSize.Small} style={{ marginTop: 10 }}>
              {`${getUnitMembersCount()} ${t(
                "OrganizationalStructure.Workers",
              )}`}
            </Text>
          )}
        </Container1>
        <Container2>
          {props.data.name !== "Unhandled" && (
            <CustomIcon
              className="icon"
              name="settings"
              size="24px"
              padding="2px"
              color={Color.DarkGrey}
              style={{ marginRight: 25, cursor: "pointer" }}
              onClick={() => setShowEditModal(true)}
            />
          )}
          {props.type === "unit" && (
            <CustomIcon
              name={props.isOpen ? "upward_arrow" : "downward_arrow"}
              size="20px"
              color={Color.BurntSienna}
              onClick={handleToggleDetails}
            />
          )}
        </Container2>
      </Row>
      {showEditModal && (
        <EditModal
          setShowEditModal={setShowEditModal}
          showEditModal={showEditModal}
          type={isUnitData ? "Unit" : "Team"}
          id={props.data.id}
          name={props.data.name}
          parentId={
            isUnitData
              ? (props.data as Unit).companyEntityId
              : (props.data as Team).companyUnitId
          }
        />
      )}
    </>
  );
};
